import {
  getBrowseResultKey,
  getBrowseResultName,
} from "@/web/routes/browse/-components/mapBrowseResults";
import type {
  BrowseResult,
  BrowseSearch,
  BrowseSort,
  DefaultStage,
} from "@/web/routes/browse/-components/types";

/**
 * Stages a result is shown under in the tab filters. A claimed nomination that
 * has also published a sharable card lives in *both* the "nomination" and
 * "roadshow" tabs — its underlying nomination is still meaningful even though
 * the SMB has progressed to the next stage.
 */
export function getBrowseResultStages(result: BrowseResult): DefaultStage[] {
  if (result.type === "roadshow") return ["roadshow"];
  if (result.type === "offering") return ["offering"];
  if (result.type === "sharableBusinessCard") return ["roadshow"];
  if (result.hasSharableBusinessCard) return ["nomination", "roadshow"];
  return ["nomination"];
}

/** Primary stage — used for badge/sort purposes where a single answer is needed. */
export function getBrowseResultStage(result: BrowseResult): DefaultStage {
  const [primary] = getBrowseResultStages(result);
  return primary ?? "nomination";
}

function matchesStageFilter(result: BrowseResult, stages: BrowseSearch["stages"]): boolean {
  if (!stages?.length) return true;
  return getBrowseResultStages(result).some((s) => stages.includes(s));
}

function matchesCategoryFilter(
  result: BrowseResult,
  categories: BrowseSearch["categories"],
): boolean {
  if (!categories?.length) return true;

  const businessType = (() => {
    if (result.type === "roadshow") return result.campaign.businessType;
    if (result.type === "offering" || result.type === "sharableBusinessCard") {
      return result.sharableBusinessCard.businessType;
    }
    return result.nominatedBusiness.businessType;
  })();

  if (!businessType) return false;

  return categories.some(
    (category) => businessType === category || businessType.startsWith(`${category}.`),
  );
}

function matchesFullTextFilter(result: BrowseResult, fullText: BrowseSearch["fullText"]): boolean {
  const query = fullText?.trim().toLowerCase();
  if (!query) return true;

  const searchable = (() => {
    if (result.type === "offering") {
      return [
        result.sharableBusinessCard.name,
        result.offeringName,
        result.location,
        result.businessTypeText,
      ];
    }
    if (result.type === "roadshow") {
      return [result.title, result.location, result.businessTypeText, result.launchedBy];
    }
    if (result.type === "sharableBusinessCard") {
      return [result.sharableBusinessCard.name, result.location, result.businessTypeText];
    }
    return [
      result.nominatedBusiness.name,
      result.location,
      result.businessTypeText,
      result.nominatedByName,
    ];
  })();

  return searchable.some((value) => value?.toLowerCase().includes(query));
}

export function filterBrowseResults(results: BrowseResult[], search: BrowseSearch): BrowseResult[] {
  return results.filter(
    (result) =>
      matchesStageFilter(result, search.stages) &&
      matchesCategoryFilter(result, search.categories) &&
      matchesFullTextFilter(result, search.fullText),
  );
}

/**
 * Order browse results for display. Pure so every surface that shows browse
 * results (the `/browse` page, the homepage's featured rail) agrees on "first".
 */
export function sortBrowseResults(results: BrowseResult[], sort: BrowseSort | undefined) {
  if (sort === "alphabetical" || sort === "alphabetical-desc") {
    const dir = sort === "alphabetical-desc" ? -1 : 1;
    return [...results].sort((a, b) => {
      const nameA = getBrowseResultName(a).toLowerCase();
      const nameB = getBrowseResultName(b).toLowerCase();
      return nameA.localeCompare(nameB) * dir;
    });
  }
  // "recent" (default): newest first by row creation time. The raw view
  // iteration order is the storage/index-scan order, which a mutating write
  // (e.g. an EOI bumping `totalInterested`) reshuffles — so we sort on the
  // immutable `createdAt` instead, with the stable result key as the
  // tiebreak so equal timestamps never reorder.
  return [...results].sort((a, b) => {
    if (b.createdAt !== a.createdAt) return b.createdAt - a.createdAt;
    return getBrowseResultKey(a).localeCompare(getBrowseResultKey(b));
  });
}
